import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { ACTIVITY_CHIPS, type ActivityChip } from "@/types/activity"

interface ActivityFilterChipsProps {
  selected: ActivityChip
  onSelect: (chip: ActivityChip) => void
}

/**
 * OKS-548 — Activity feed filter chips (UX Spec §2).
 *
 * Horizontal, scrollable pill row: All · Kudos · Cheers · Missions ·
 * Badges. `All` is the default. Filtering is client-side (KD-6) — the
 * page owns the selected state and re-derives the visible rows.
 *
 * Accessibility: exposed as a labelled radio group (`role="radiogroup"`
 * + `aria-checked`) so the selected state reaches AT without relying on
 * color alone (UX Spec §6).
 */
export function ActivityFilterChips({
  selected,
  onSelect,
}: ActivityFilterChipsProps) {
  const { t } = useTranslation()

  return (
    <div
      role="radiogroup"
      aria-label={t("activity.filters.label")}
      data-testid="activity-filter-chips"
      className="flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {ACTIVITY_CHIPS.map((chip) => {
        const isSelected = chip === selected
        return (
          <button
            key={chip}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-testid={`activity-chip-${chip}`}
            data-selected={isSelected ? "true" : undefined}
            onClick={() => onSelect(chip)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors outline-none",
              "focus-visible:ring-ring focus-visible:ring-2",
              isSelected
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-muted/60",
            )}
          >
            {t(`activity.filters.${chip}`)}
          </button>
        )
      })}
    </div>
  )
}
